import { Fragment } from "react";
import { Icon, type IconName } from "../components/Icon";
import { catalogTracks, resolveContentPart } from "../lib/catalog";
import { contentHash, routeHash } from "../lib/routing.mjs";
import type { LanguagePack, Page, Route, Selection } from "../types";
import { useAuth } from "./AuthGate";

const ITEMS: { id: Page; label: string; glyph: IconName }[] = [
  { id: "home", label: "Home", glyph: "home" },
  { id: "part", label: "Content", glyph: "book" },
  { id: "study", label: "Study", glyph: "book" },
  { id: "audio", label: "Audio Practice", glyph: "audio" },
  { id: "stories", label: "Stories", glyph: "stories" },
  { id: "stats", label: "Stats", glyph: "stats" },
];
const LABELS: Partial<Record<Page, string>> = { study: "Study", audio: "Audio Practice", stories: "Stories", stats: "Stats" };

export interface Nav {
  languageId: string | null;
  name?: string;
  page: Page | "languages";
  items: { id: Page; label: string; glyph: IconName; href: string }[];
  mobileItems: { id: Page; label: string; glyph: IconName; href: string }[];
  crumbs: [string, string][];
  /** The content part being studied, shown in the sidebar so it can be switched. */
  part: { collection: string; title: string; href: string } | null;
}

/** Navigation for the current route: top-level links outside a content part, mode links inside one. */
export function buildNav(route: Route, pack: LanguagePack | null, selection: Selection | null, fallbackName?: string): Nav {
  if (route.page === "languages") return { languageId: null, page: "languages", items: [], mobileItems: [], crumbs: [], part: null };
  const languageId = route.languageId;
  const name = pack?.definition.name ?? fallbackName;
  const selected = pack && selection ? resolveContentPart(pack, selection) : null;
  const page = route.page;
  const href = (id: Page) => (id === "home" || id === "stats" ? routeHash(languageId, id) : contentHash(languageId, selection ?? {}, id));
  const showModes = !!selected && ["part", "study", "audio", "stories"].includes(page);
  const items = ITEMS.filter((n) => showModes || n.id === "home" || n.id === "stats").map((n) => ({ ...n, href: href(n.id) }));

  const crumbs: [string, string][] = [["Languages", "#/languages"], [name ?? languageId, routeHash(languageId)]];
  const path: Selection | null = route.trackId ? route : showModes ? selection : null;
  if (path && pack) {
    const track = catalogTracks(pack).find((t) => t.id === path.trackId);
    if (track) crumbs.push([track.title, contentHash(languageId, { trackId: track.id }, "catalog")]);
    const collection = track?.collections.find((c) => c.id === path.collectionId);
    if (track && collection) crumbs.push([collection.title, contentHash(languageId, { trackId: track.id, collectionId: collection.id }, "catalog")]);
    if (selected && path.partId) crumbs.push([selected.part.title, href("part")]);
  }
  if (LABELS[page]) crumbs.push([LABELS[page]!, href(page)]);

  return {
    languageId, name, page, items, crumbs,
    mobileItems: showModes ? items.filter((n) => n.id !== "home") : items,
    part: showModes && selected ? { collection: selected.collection.title, title: selected.part.title, href: href("part") } : null,
  };
}

const Brand = () => (<><span className="brand-mark" aria-hidden="true">響</span><span>hibiki</span></>);
const homeHref = (languageId: string | null) => (languageId ? routeHash(languageId) : "#/languages");

export function Sidebar({ nav }: { nav: Nav }) {
  const { languageId, page } = nav;
  return (
    <aside className="sidebar" id="sidebar">
      <a className="brand" href={homeHref(languageId)} aria-label="Hibiki home"><Brand /></a>
      <p className="brand-caption">Make room for a language.</p>
      {languageId && (
        <>
          <nav className="desktop-navigation" aria-label="Main navigation">
            {nav.items.map((n) => (
              <a key={n.id} href={n.href} className={"nav-item " + (page === n.id ? "active" : "")} aria-current={page === n.id ? "page" : undefined}>
                <Icon name={n.glyph} /><span>{n.label}</span>
              </a>
            ))}
          </nav>
          {nav.part ? (
            <a className="content-switch" href={nav.part.href}>
              <span className="overline">{nav.part.collection}</span><strong>{nav.part.title}</strong>
              <small><Icon name="repeat" /> Change content</small>
            </a>
          ) : (
            <div className="sidebar-note">
              <span className="tiny-wave" aria-hidden="true"><i /><i /><i /><i /><i /></span>
              <p>A little practice.<br />A little more familiar.</p>
            </div>
          )}
          <a className="language-switch" href="#/languages">
            <Icon name="globe" /><span><strong>{nav.name}</strong><small>Change language</small></span><Icon name="next" />
          </a>
        </>
      )}
      <SignOut />
    </aside>
  );
}

export function Topbar({ nav }: { nav: Nav }) {
  const { languageId, name } = nav;
  return (
    <header className="topbar" id="topbar">
      <a className="mobile-brand" href={homeHref(languageId)}><Brand /></a>
      {languageId ? (
        <>
          <nav className="breadcrumb" aria-label="Breadcrumb">
            {nav.crumbs.map(([title, url], i) => (
              <Fragment key={url + i}>{i > 0 && <span aria-hidden="true">/</span>}<a href={url}>{title}</a></Fragment>
            ))}
          </nav>
          <a className="top-language" href="#/languages"><Icon name="globe" />{name}</a>
        </>
      ) : <span className="top-note">Learn in your own rhythm</span>}
    </header>
  );
}

export function BottomNavigation({ nav }: { nav: Nav }) {
  return (
    <nav className="bottom-navigation" id="bottom-navigation" aria-label="Main navigation" hidden={!nav.mobileItems.length}>
      {nav.mobileItems.map((n) => (
        <a key={n.id} href={n.href} className={nav.page === n.id ? "active" : ""} aria-current={nav.page === n.id ? "page" : undefined}>
          <Icon name={n.glyph} /><span>{n.label}</span>
        </a>
      ))}
    </nav>
  );
}

function SignOut() {
  const { email, signOut } = useAuth();
  if (!email) return null;
  return <button className="text-link" onClick={signOut} title={email}>Sign out</button>;
}
